import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import sortBy from 'lodash/sortBy';
import throttle from 'lodash/throttle';
import min from 'lodash/min';

import customerTimelineApi from 'api/timeline';
import customerNotesApi from 'api/customerNotes';

import TimelineModel from 'models/timeline';

import TimelineEvent from './timelineEvent';
import TimelineNote from './timelineNote';
import Loader from 'components/loader';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';

import {getTypeColor, getFiltered, getDetailsLink} from 'helpers/timelineFiltering';
import {typeEventFilters, POSTypes} from 'helpers/timelineConstants';

// Libs
import events from 'lib/events';
import {dateTimeFormats} from 'helpers/constants';

class Timeline extends React.Component {
  static displayName = 'Timeline';

  static propTypes = {
    customer: PropTypes.any,
    id: PropTypes.any,
    intl: intlShape.isRequired,
    pos: PropTypes.any,
  };

  state = {
    timelinePageSize: 20,
    noteActive: false,
    loaded: false,
    timelinePageIndex: 0,
    timelineLoading: false,
    timelineGotAll: false,
    events: [],
    currentPools: [],
    currentCustomerContacts: [],

    showFilters: false,
    eventTypes: typeEventFilters,
    eventFilter: '',
    contacts: [{id: null, label: null, email: null}],
    contactFilter: {id: '', label: '', email: ''},
    startDate: moment().subtract(6, 'month'),
    appliedFilters: null,
    endDate: moment(),
    sortByAsc: false,
  };

  componentWillMount() {
    const {customer} = this.props;

    this.getInitalTimelineEvents(false);

    const currentPools = (customer.pools || []).map(p => p.id);
    const currentCustomerContacts = (customer.customerContacts || []).map(c => ({
      name: `${c.nameFirst} ${c.nameLast}`,
      email: c.email,
    }));

    this.setState({
      currentPools: currentPools,
      currentCustomerContacts: currentCustomerContacts,
    });
  }

  componentDidMount() {
    window.addEventListener('wheel', this.scrollGetTimeline, {capture: false, passive: true});
    window.addEventListener('scroll', this.scrollGetTimeline, {capture: false, passive: true});
  }

  componentWillUnmount() {
    window.removeEventListener('wheel', this.scrollGetTimeline, {capture: false, passive: true});
    window.removeEventListener('scroll', this.scrollGetTimeline, {capture: false, passive: true});
  }

  getEventLabel = event => {
    const {formatMessage} = this.props.intl;
    const {eventTypes} = this.state;

    return (eventTypes.find(e => event && e.types && e.types.toString() === event.toString()) ||
      {label: formatMessage({id: 'customer.timeline.all.events'})})
      .label;
  };

  getEvents = () => {
    const {pos} = this.props;
    let eventsArray = [];

    if (this.state.events.length > 0) {
      //Apply Filters
      let filteredEvents = getFiltered(this.state.events, [{filter: 'event', value: this.state.eventFilter}], pos);
      // Sort events by ASC/DESC

      filteredEvents = sortBy(filteredEvents, 'created');

      if (!this.state.sortByAsc) {
        filteredEvents = filteredEvents.reverse();
      }

      //Filter dates and POS
      filteredEvents = filteredEvents.filter((t) => {
        if (!pos && POSTypes.includes(t.type)) {
          return false;
        }
        const start = moment(t.created).isAfter(this.state.startDate) || moment(t.created).isSame(this.state.startDate, 'day');
        const end = moment(t.created).isBefore(this.state.endDate) || moment(t.created).isSame(this.state.endDate, 'day');

        return start && end;
      });

      filteredEvents.forEach((t, i) => {
        eventsArray.push(
          <TimelineEvent
            key={i}
            event={t}
            scroll={this.state.scroll}
            color={getTypeColor(t.type)}
            customerContacts={this.state.currentCustomerContacts}
            link={getDetailsLink(t, this.state.currentPools)}
            onLastShow={i === filteredEvents.length - 1 ? this.lastChildVisible : null}
          />
        );
      });
    }
    return eventsArray;
  };

  getInitalTimelineEvents = (sort = false) => {
    this.setState({
      timelineLoading: true,
      events: [],
    });

    customerTimelineApi.getList(this.props.id, this.state.timelinePageSize, 0, sort)
      .then(timeline => {
        this.setState({
          events: [
            ...timeline,
            ...this.makeAnniversaryEvents(timeline.length, this.props.customer.created),
          ],
          loaded: true,
          timelinePageIndex: 0,
          sortByAsc: sort,
          startDate: this.getTimelineStartDate(timeline),
          timelineLoading: false,
        });
      })
      .catch(err => {
        console.error(err);
        events.publish('error.user', {message: this.props.intl.formatMessage({id: 'customer.error.get.timeline'})});
      });
  };

  // Gets earliest start date of array of timelines
  getTimelineStartDate = events => min(events.filter(e => e.type !== 68).map(e => e.created));


  lastChildVisible = () => {
    this.scrollGetTimeline(null, true);
  };

  // Generate fake anniversary events based on customer.created
  makeAnniversaryEvents = (events, date) => {
    const {formatMessage} = this.props.intl;
    let now = moment();
    let anDate = date.add(1, 'y');
    let count = 1;
    let anniversaries = [];

    while (anDate.isBefore(now)) {
      const anniversaryEvent = new TimelineModel({
        id: null,
        customerID: this.props.id,
        type: 70,
        action: 1,
        userEmail: 'System',
        data: {
          data: {},
          title: formatMessage({id: 'customer.timeline.year.anniversary'}, {year: count}),
        },
        created: anDate,
      });

      anniversaries.push(anniversaryEvent);
      anDate = anDate.add(1, 'y');
      count++;
    }

    return events > 0 ? anniversaries : [];
  };

  saveNote = note => {
    customerNotesApi.save(note, this.props.id)
      .then(() => {
        this.setState({noteActive: false});
        this.getInitalTimelineEvents(false);
      })
      .catch(err => {
        console.error(err);
        events.publish('error.user', {message: this.props.intl.formatMessage({id: 'customer.error.save.notes'})});
      });
  };

  // Called when last timeline event is rendered
  scrollGetTimeline = (e, call) => {
    // Throttle calls to get more previous messages
    const callInfinite = throttle(() => {
      this.setState({
        timelineLoading: true,
      });
      customerTimelineApi.getList(this.props.id, this.state.timelinePageSize, this.state.timelinePageIndex + 1, this.state.sortByAsc)
        .then((timeline) => {
          if (timeline.length === 0) {
            this.setState({
              timelineLoading: false,
              timelineGotAll: true,
            });
          } else {
            this.setState({
              events: [
                ...this.state.events,
                ...timeline,
              ],
              startDate: this.getTimelineStartDate(this.state.events.concat(timeline)),
              timelineLoading: false,
              timelinePageIndex: this.state.timelinePageIndex + 1,
            });
          }
        });
    }, 1000, {'leading': true});

    if (call) {
      callInfinite();
    }

    // Calculate scroll position and pass to children
    const passScrollToChildren = throttle(() => {
      this.setState({
        scroll: document.body.scrollTop,
      });
    }, 50, {'leading': true});

    passScrollToChildren();
  };

  scrollTopTimeline = () => {
    window.scroll(0, 0);
  };

  stateSetter = propName => value => this.setState({[propName]: value});
  toggleAsendingEvents = () => {
    this.getInitalTimelineEvents(!this.state.sortByAsc);
  };

  render() {
    const timelineEvents = this.getEvents();

    return <div id='timeline'
                className='row'>
      <Loader loaded={this.state.loaded}>
        <div className='col-xs-12'>
          <section className='timeline-filters'>
            <div className={'timeline-collapsed ' + (this.state.showFilters ? 'hide-filter' : '')}>
              <ul>
                <li>
                  <FormattedMessage id="general.timeline"/>
                </li>
                <li>
                  <FormattedMessage id='general.date.range'
                                    values={{
                                      start: moment(this.state.startDate).format(dateTimeFormats.numeralDate),
                                      end: moment(this.state.endDate).format(dateTimeFormats.numeralDate),
                                    }}/>
                </li>
                <li>
                  {this.getEventLabel(this.state.eventFilter)}
                </li>
              </ul>
              <div className={'filter-dropdown-button'}>
                <i onClick={() => this.setState({showFilters: !this.state.showFilters})}
                   className={'bl bl-dropdown'}/>
              </div>
            </div>
            <div className={'timeline-filters-expanded ' + (this.state.showFilters ? '' : 'hide-filter')}>
              <div className='timeline-date'>
                <div className='pull-left'>
                  <div className='filter-title'><FormattedMessage id="general.date.from"/></div>
                  <DatePicker selected={this.state.startDate}
                              dateFormat='L'
                              maxDate={this.state.endDate.clone().subtract(1, 'days')}
                              onChange={this.stateSetter('startDate')}/>
                </div>
                <div className='pull-left'>
                  <div className='filter-title'><FormattedMessage id="general.date.to"/></div>
                  <DatePicker selected={this.state.endDate}
                              dateFormat='L'
                              onChange={this.stateSetter('endDate')}/>
                </div>
              </div>
              <div className='timeline-filter-by'>
                <div className='pull-right filter-selector'>
                  <div className='filter-title'><FormattedMessage id="customer.timeline.event.filter"/></div>
                  <select value={this.state.eventFilter}
                          onChange={e => this.setState({eventFilter: e.target.value})}
                          data-event-category='User'
                          data-event-action='sel-problem-type'>
                    <option value=''><FormattedMessage id="customer.timeline.all.events"/></option>
                    {(this.state.eventTypes || []).map(f => <option key={f.label}
                                                                    value={f.types}>{f.label}</option>)}
                  </select>
                </div>
              </div>
              <div className={'filter-dropdown-button'}>
                <i onClick={() => this.setState({showFilters: !this.state.showFilters})}
                   className={'bl bl-dropdown spin-chev'}/>
              </div>
            </div>
          </section>

          <section className='timeline-notes-header'>
            <div className='title'>
              <FormattedMessage id="customer.timeline.title"/>
            </div>
            <button className='btn btn-primary add-button'
                    onClick={() => this.setState({noteActive: true})}>
              <i className='bl bl-add'/>
              <FormattedMessage id="customer.add.note"/>
            </button>
          </section>

          <section className='timeline-header'>
            {this.state.noteActive ? this.props.intl.formatMessage({id: 'general.notes'}) : null}
          </section>

          {this.state.noteActive ?
            <section className='timeline-note'>
              <TimelineNote customerID={this.props.id}
                            onSave={this.saveNote}
                            onCancel={() => this.setState({noteActive: false})}/>
            </section> :
            null}
        </div>
        <section>
          <div className='col-xs-12'>
            <section id='cd-timeline'
                     className='cd-container'>
              {timelineEvents.length > 0 ?
                <div>
                  <div className='direction-sorting'>
                    <i className={'bl icon pointer bl-' + (this.state.sortByAsc ? 'DirectionOfEvents-Up' : 'DirectionOfEvents-Down')}
                       onClick={this.toggleAsendingEvents}/>
                  </div>
                  {timelineEvents}
                  <Loader loaded={!this.state.timelineLoading}/>
                </div> :
                <Loader loaded={!this.state.timelineLoading}>
                  <section className='col-xs-12'>
                    <div className='no-results'>
                      <FormattedMessage id="general.error.no.results"/><i className='bl bl-attention'/></div>
                  </section>
                </Loader>}
            </section>
          </div>
          <div className='footer-button'
               onClick={this.scrollTopTimeline}>
            <i className='bl bl-dropdown spin-chev'/>
            <div><FormattedMessage id="customer.timeline.back.top"/></div>
          </div>
        </section>
      </Loader>
    </div>;
  }
}

export default injectIntl(Timeline);
